/** Image URLs, composed the way the app composes them.
 *
 * `AndroidImageMapper.map`:
 *   key = isSvg || !resize ? "<prefix>/<suffix>" : "<prefix>/inside_<screenWidth>/<suffix>"
 *   return settings.base + URLEncoder.encode(key, "UTF-8")
 *
 * Two things that look like bugs and are not:
 *  - the WHOLE key is percent-encoded, slashes included, so the URL contains `%2F`;
 *  - `screenWidth` is displayMetrics.widthPixels — PHYSICAL pixels, not dp.
 *
 * Spec §6.1.
 */

/** Java's URLEncoder, which JS's encodeURIComponent is not: space becomes `+`, and
 *  only `A-Za-z0-9.-*_` survive unescaped (so `*` stays, `~` and `!` do not). */
export function javaUrlEncode(value: string): string {
  let out = '';
  for (const ch of value) {
    if (/[A-Za-z0-9.\-*_]/.test(ch)) {
      out += ch;
    } else if (ch === ' ') {
      out += '+';
    } else {
      for (const byte of new TextEncoder().encode(ch)) {
        out += `%${byte.toString(16).toUpperCase().padStart(2, '0')}`;
      }
    }
  }
  return out;
}

export interface DomainImage {
  /** key prefix — `p` on the wire */
  prefix: string;
  /** key suffix — `s` on the wire */
  suffix: string;
}

export const isSvg = (image: DomainImage): boolean => /svg$/i.test(image.suffix);

export function imageUrl(
  base: string,
  image: DomainImage | null | undefined,
  widthPx: number,
  resize = true,
): string | null {
  if (!image?.prefix || !image.suffix) return null;
  const key =
    isSvg(image) || !resize
      ? `${image.prefix}/${image.suffix}`
      : `${image.prefix}/inside_${widthPx}/${image.suffix}`;
  return base + javaUrlEncode(key);
}

/** `{p, s}` is how every image arrives in the payload. */
export function toDomainImage(raw: unknown): DomainImage | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const prefix = typeof o.p === 'string' ? o.p : null;
  const suffix = typeof o.s === 'string' ? o.s : null;
  return prefix && suffix ? { prefix, suffix } : null;
}
